import * as React from 'react'
import { X } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import {
  FURNITURE_TAGS_PER_ITEM_MAX,
  FURNITURE_TAG_MAX,
  addFurnitureTag,
  matchExistingTag,
} from '@lib/helpers/furniture-tag'

/**
 * The tags on one piece of furniture: chips inside the field, with room to type
 * another after them.
 *
 * The chips sit *in* the field rather than under it, so the field reads as
 * holding the tags rather than as a box with a list beneath it. Each chip is
 * the tag as it will be *stored* — settled on the same shared helpers the API
 * settles with, so a venue's own spelling is adopted while somebody is looking
 * at it rather than on submit.
 */
export function TagField({
  id,
  value,
  suggestions,
  onChange,
  placeholder,
}: {
  id: string
  value: readonly string[]
  /** Tags already in use, offered below and used to settle spelling. */
  suggestions: readonly string[]
  onChange: (next: string[]) => void
  placeholder?: string
}) {
  const [draft, setDraft] = React.useState('')
  const inputRef = React.useRef<HTMLInputElement>(null)
  const listId = `${id}-suggestions`
  const full = value.length >= FURNITURE_TAGS_PER_ITEM_MAX

  function commit(raw: string): void {
    const next = addFurnitureTag(value, raw, suggestions)
    setDraft('')
    if (next.length !== value.length) onChange(next)
  }

  function remove(tag: string): void {
    onChange(value.filter((t) => t !== tag))
    // Removing a tag is nearly always the start of typing a different one.
    window.requestAnimationFrame(() => inputRef.current?.focus())
  }

  /** Only what this piece does not already carry — suggesting a tag it has is noise. */
  const offerable = suggestions.filter((s) => matchExistingTag(s, value) === null)

  return (
    <>
      <div className="flex min-h-10 w-full flex-wrap items-center gap-1.5 rounded-md border border-line-strong bg-surface px-2 py-1.5 transition-colors focus-within:focus-ds hover:border-line-strong">
        {value.map((tag) => (
          <Badge key={tag} className="gap-1 pr-1">
            {tag}
            <button
              type="button"
              onClick={() => remove(tag)}
              aria-label={`Remove tag ${tag}`}
              className="rounded-sm p-0.5 text-current opacity-70 transition-opacity hover:opacity-100 focus-visible:focus-ds"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
        <Input
          id={id}
          ref={inputRef}
          list={listId}
          value={draft}
          disabled={full}
          maxLength={FURNITURE_TAG_MAX}
          placeholder={full ? 'That is plenty of tags' : value.length === 0 ? placeholder : 'Add another…'}
          onChange={(e) => {
            const next = e.target.value
            setDraft(next)
            // Picking from the datalist commits straight away; typing does not,
            // even when what has been typed so far happens to be a whole tag.
            // Matching on the value alone made any tag beginning with an
            // existing one impossible to type: with "Vendor-Rental" in use,
            // "Vendor-Rental (linens)" committed itself halfway through and
            // left " (linens)" behind as a second tag. A pick is an
            // `insertReplacementText` input; a keystroke is not.
            const inputType = 'inputType' in e.nativeEvent ? e.nativeEvent.inputType : undefined
            if (inputType === 'insertReplacementText' && suggestions.some((s) => s === next)) {
              commit(next)
            }
          }}
          onBlur={() => commit(draft)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              // Enter in a form submits it, and adding a tag is not saving the
              // piece.
              e.preventDefault()
              commit(draft)
            }
            // Backspace on an empty box takes the last chip back, the one thing
            // every chip field does and people expect without being told.
            if (e.key === 'Backspace' && draft === '' && value.length > 0) {
              const last = value[value.length - 1]
              if (last === undefined) return
              e.preventDefault()
              remove(last)
            }
          }}
          // The chips are the field's border; a second one around the input
          // would draw a box inside a box.
          className="h-7 flex-1 border-0 bg-transparent px-1 focus-visible:outline-none focus-visible:ring-0"
        />
      </div>
      <datalist id={listId}>
        {offerable.map((s) => (
          <option key={s} value={s} />
        ))}
      </datalist>
    </>
  )
}
